import React, {
  Ref,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useCallback,
  IframeHTMLAttributes,
} from 'react';
import styled from 'styled-components';
import { nanoid } from 'nanoid';

import {
  IFrameEventName,
  IFrameDocumentSaveSummaryMeta,
  IFrameFieldMeta,
  IFrameModalMeta,
  IFrameErrorMeta,
  IFrameEvent,
  IFrameDocumentSavedMeta,
  IFrameMessageName,
  IFrameDocumentSaveSummary,
  IFrameModalEventName,
  IFrameHasChangesMeta,
  IFrameActiveDocumentChanged,
  IFrameDownloadMeta,
  IFrameShareMeta,
  IFrameDocumentsLoaded,
} from 'zdocs-web-core/lib/iframe-events';
import useCallbackRef from 'zdocs-web-core/hooks/useCallbackRef';
import { assertNever } from 'zdocs-web-core/lib/utils/type-guards';

const ONE_MINUTE = 60000;
const MESSAGE_EVENT = 'message';
const EDITOR_VERSION = import.meta.env.VITE_SHA || '0';
const StyledIFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
`;

function postIFrameMessage(
  iFrame: HTMLIFrameElement | null | undefined,
  targetOrigin: string,
  messageName: IFrameMessageName,
  metaData?: any
) {
  iFrame?.contentWindow?.postMessage(
    {
      event: messageName,
      meta: metaData,
    },
    targetOrigin
  );
}

type SaveRequest = {
  id: string;
  timeoutId: number;
  resolve: (value: IFrameDocumentSaveSummary) => void;
  reject: (reason?: any) => void;
};
type SaveFinishedCallback = (
  success: boolean,
  saveSummary: ZDocsEditorSaveSummary
) => void;
type EventCallback = () => void;
type DocumentEventCallback = (documentId: string) => void;
type FieldEventCallback = (field: ZDocsEditorFieldInfo) => void;
type ModalVisibilityChangeCallback = (
  modal: ZDocsEditorModalName,
  open: boolean
) => void;
type HasChangesCallback = (hasChanges: boolean) => void;
type CanUndoChangesCallback = (canUndo: boolean) => void;
type CanRedoChangesCallback = (canRedo: boolean) => void;
type DownloadClickedCallback = (fileUrl: string) => void;
type ShareClickedCallback = (fileUrl: string) => void;
type DocumentLoadedEventCallback = (documents: ZDocsEditorDocuments) => void;

export type ZDocsEditorError = IFrameErrorMeta;
export type ZDocsEditorFieldInfo = IFrameFieldMeta;
export type ZDocsEditorModalName = IFrameModalEventName;
export type ZDocsEditorSaveSummary = IFrameDocumentSaveSummary;
export type ZDocsEditorDocuments = IFrameDocumentsLoaded;
export type Props = {
  /**
   * URL for the iFrame editor session
   */
  url: string;
  onEditorError?: (error: ZDocsEditorError) => void;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when a field is placed on the document
   * @param field Field meta data of the field that was added
   */
  onFieldPlaced?: FieldEventCallback;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when an existing field is clicked in the document
   *
   * @param field Field meta data of the field that was clicked
   */
  onFieldClicked?: FieldEventCallback;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when an existing field is removed from the document
   *
   * @param field Field meta data of the field that was removed
   */
  onFieldRemoved?: FieldEventCallback;
  /**
   * Is triggered when a modal changes visibility either
   * opened -> close or closed -> open
   *
   * @param modal the name of the modal that triggered the event
   * @param open whether the modal is now open or not
   */
  onModalVisibilityChange?: ModalVisibilityChangeCallback;
  /**
   * Is triggered when 'enforceScroll' is set to true,
   * user must scroll to the bottom before signing.
   * This fires when the user has scrolled to the bottom
   */
  onReviewCompleted?: () => void;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when the state of the editor having changes.
   *
   * @param hasChanges Denotes if there are changes when this callback fires
   */
  onHasChanges?: HasChangesCallback;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when the state of whether changes that can be undone changes
   *
   * @param canUndo Denotes if there are changes when that can be undone
   */
  onCanUndoChanges?: CanUndoChangesCallback;
  /**
   * Triggered only when editorApp = TEMPLATE_EDITOR
   * Is triggered when the state of whether changes that can be redone changes
   *
   * @param canUndo Denotes if there are changes when that can be redone
   */
  onCanRedoChanges?: CanRedoChangesCallback;
  /**
   * Is triggered when the user token has expired
   */
  onTokenExpired?: EventCallback;
  /**
   * Is triggered when the document has been persisted and a field transaction was created
   *
   * @param documentId The documentId of the document that was saved
   */
  onDocumentSaved?: DocumentEventCallback;
  /**
   * Is triggered when all saves have finished for the session.
   * This can be used to know the user has finished their work in the session if success is true.
   *
   * @param success Whether all the saves were completed successfully
   * @param saveSummary A map of documentId -> saveSuccess to know which documents saved successfully or not
   */
  onSaveFinished?: SaveFinishedCallback;
  /**
   * Triggered only when editorApp = DEFAULT
   * Is triggered when the user saves their progress successfully
   */
  onProgressSaveFinished?: EventCallback;
  /**
   * Is triggered when the document revision has changed from when the user first entered the session.
   * When this happens, the user may have had to resolve conflicts when saving.
   */
  onDocumentRevisionRefresh?: EventCallback;
  /**
   * Is triggered every to denote the user is interacting with the editor.
   * Sends in 60s chunks if activity happened.
   */
  onUserActivity?: EventCallback;
  /**
   * Is triggered when the user views a different document in the editor
   * @param documentId The documentId for the document being actively viewed in the editor
   */
  onActiveDocumentChanged?: DocumentEventCallback;
  /**
   * Is triggered when the user clicks the download button in the editor.
   */
  onDownloadClicked?: DownloadClickedCallback;
  /**
   * Is triggered when the user clicks the share button in the editor.
   */
  onShareClicked?: ShareClickedCallback;
  /**
   * Is triggered when documents are loaded
   */
  onDocumentsLoaded?: DocumentLoadedEventCallback;
} & Pick<
  IframeHTMLAttributes<HTMLIFrameElement>,
  'id' | 'className' | 'title' | 'name' | 'onLoad' | 'onError'
>;
export type ZDocsEditorActions = {
  /**
   * Will cause the iFrame to reload
   */
  reload: () => void;
  /**
   * Only applicable when editorApp = TEMPLATE_EDITOR
   * Will cause the editor to undo the last change.
   */
  undoChanges: () => void;
  /**
   * Only applicable when editorApp = TEMPLATE_EDITOR
   * Will cause the editor to redo the last change.
   */
  redoChanges: () => void;
  /**
   * Will trigger the editor to save a field data transaction.
   *
   * __note__: Will timeout if save takes longer than 60s
   *
   * @return Returns a promise that when fulfilled, returns the
   * save summary of a map of documentId -> success.
   */
  save: () => Promise<IFrameDocumentSaveSummary>;
  /**
   * Will trigger the editor to save progress if the session is opted into progress saving.
   *
   * __note__: "enableProgressSave" must be set to true under "editorOptions" on the actions/editor request.
   */
  saveProgress: () => void;
};

const ZDocsEditor = forwardRef(function ZDocsEditor(
  {
    url,
    id,
    className = '',
    title,
    name,
    onLoad,
    onError,
    onEditorError,
    onFieldPlaced,
    onFieldClicked,
    onFieldRemoved,
    onModalVisibilityChange,
    onReviewCompleted,
    onHasChanges,
    onCanUndoChanges,
    onCanRedoChanges,
    onTokenExpired,
    onDocumentSaved,
    onSaveFinished,
    onProgressSaveFinished,
    onDocumentRevisionRefresh,
    onActiveDocumentChanged,
    onUserActivity,
    onDownloadClicked,
    onShareClicked,
    onDocumentsLoaded,
  }: Props,
  ref: Ref<ZDocsEditorActions>
) {
  const [iFrame, setIFrame] = useCallbackRef<HTMLIFrameElement>(null);
  const saveRequestRef = useRef<SaveRequest | undefined>();
  const sentEditorVersionRef = useRef(false);
  const targetOrigin = new URL(url).origin;
  const trySendEditorVersion = useCallback(() => {
    if (!sentEditorVersionRef.current) {
      postIFrameMessage(iFrame, targetOrigin, IFrameMessageName.EditorVersion, {
        version: EDITOR_VERSION,
      });
      sentEditorVersionRef.current = true;
    }
  }, [iFrame, targetOrigin]);
  const handleModalVisibilityEvent = useCallback(
    (eventData: IFrameEvent<IFrameEventName>) => {
      const { event, meta } = eventData;
      const { name } = meta as IFrameModalMeta;
      const opened = event === IFrameEventName.ModalOpened;
      onModalVisibilityChange?.(name, opened);
    },
    [onModalVisibilityChange]
  );
  const handleHasChangesEvent = useCallback(
    (eventData: IFrameEvent<IFrameEventName>) => {
      const { event, meta } = eventData;
      const hasChangesMeta = meta as IFrameHasChangesMeta;
      const hasChanges = event === IFrameEventName.HasChanges;

      onHasChanges?.(hasChanges);

      if (hasChangesMeta) {
        onCanUndoChanges?.(hasChangesMeta.canUndo);
        onCanRedoChanges?.(hasChangesMeta.canRedo);
      }
    },
    [onHasChanges, onCanUndoChanges, onCanRedoChanges]
  );
  const handleDocumentSaveSummaryEvent = useCallback(
    (eventMetaData: IFrameDocumentSaveSummaryMeta) => {
      const { success, summary, messageId } = eventMetaData;

      onSaveFinished?.(success, summary);

      if (saveRequestRef.current) {
        const { id, timeoutId, resolve, reject } = saveRequestRef.current;

        if (id === messageId) {
          clearTimeout(timeoutId);

          if (success) {
            resolve(summary);
          } else {
            reject(summary);
          }

          saveRequestRef.current = undefined;
        }
      }
    },
    [onSaveFinished]
  );
  const handlePostMessage = useCallback(
    (iFrameMessageEvent: MessageEvent<IFrameEvent<IFrameEventName>>) => {
      const { data } = iFrameMessageEvent;
      const { event, meta } = data;

      if (!event) {
        return;
      }

      trySendEditorVersion();

      switch (event) {
        case IFrameEventName.Error: {
          onEditorError?.(meta as ZDocsEditorError);
          break;
        }
        case IFrameEventName.FieldPlaced: {
          onFieldPlaced?.(meta as ZDocsEditorFieldInfo);
          break;
        }
        case IFrameEventName.FieldClicked: {
          onFieldClicked?.(meta as ZDocsEditorFieldInfo);
          break;
        }
        case IFrameEventName.FieldRemoved: {
          onFieldRemoved?.(meta as ZDocsEditorFieldInfo);
          break;
        }
        case IFrameEventName.ModalOpened:
        case IFrameEventName.ModalClosed: {
          handleModalVisibilityEvent(data);
          break;
        }
        case IFrameEventName.ReviewComplete: {
          onReviewCompleted?.();
          break;
        }
        case IFrameEventName.HasChanges:
        case IFrameEventName.HasNoChanges: {
          handleHasChangesEvent(data);
          break;
        }
        case IFrameEventName.TokenExpired: {
          onTokenExpired?.();
          break;
        }
        case IFrameEventName.DocumentSaved: {
          const { documentId } = meta as IFrameDocumentSavedMeta;
          onDocumentSaved?.(documentId);
          break;
        }
        case IFrameEventName.DocumentSaveSummary: {
          handleDocumentSaveSummaryEvent(meta as IFrameDocumentSaveSummaryMeta);
          break;
        }
        case IFrameEventName.DocumentRevisionRefresh: {
          onDocumentRevisionRefresh?.();
          break;
        }
        case IFrameEventName.UserActivity: {
          onUserActivity?.();
          break;
        }
        case IFrameEventName.ProgressSaved: {
          onProgressSaveFinished?.();
          break;
        }
        case IFrameEventName.DownloadClicked: {
          const { fileUrl } = meta as IFrameDownloadMeta;
          onDownloadClicked?.(fileUrl);
          break;
        }
        case IFrameEventName.ShareClicked: {
          const { fileUrl } = meta as IFrameShareMeta;
          onShareClicked?.(fileUrl);
          break;
        }
        case IFrameEventName.ActiveDocumentChanged: {
          const { documentId } = meta as IFrameActiveDocumentChanged;
          onActiveDocumentChanged?.(documentId);
          break;
        }
        case IFrameEventName.DocumentsLoaded: {
          onDocumentsLoaded?.(meta as ZDocsEditorDocuments);
          break;
        }
        case IFrameEventName.MlDataRequest:
        case IFrameEventName.SubmitFeedbackDataRequest: {
          // We want to be exhaustive so type checking checks if a new iFrame event is added
          // but not dealt with here.
          // This block should be for all iFrame events we don't want to expose as a public API.
          break;
        }
        default: {
          assertNever(event);
        }
      }
    },
    [
      trySendEditorVersion,
      onEditorError,
      onFieldPlaced,
      onFieldClicked,
      onFieldRemoved,
      handleModalVisibilityEvent,
      onReviewCompleted,
      handleHasChangesEvent,
      onTokenExpired,
      onDocumentSaved,
      handleDocumentSaveSummaryEvent,
      onProgressSaveFinished,
      onDocumentRevisionRefresh,
      onActiveDocumentChanged,
      onUserActivity,
      onDownloadClicked,
      onShareClicked,
      onDocumentsLoaded,
    ]
  );

  useEffect(() => {
    window.addEventListener(MESSAGE_EVENT, handlePostMessage);
    return () => window.removeEventListener(MESSAGE_EVENT, handlePostMessage);
  }, [handlePostMessage]);

  useImperativeHandle(ref, () => ({
    reload: () => {
      if (iFrame) {
        iFrame.src += '';
      }
    },
    undoChanges: () =>
      postIFrameMessage(iFrame, targetOrigin, IFrameMessageName.UndoChanges),
    redoChanges: () =>
      postIFrameMessage(iFrame, targetOrigin, IFrameMessageName.RedoChanges),
    save: () =>
      new Promise<IFrameDocumentSaveSummary>((resolve, reject) => {
        saveRequestRef.current = {
          id: nanoid(),
          timeoutId: setTimeout(() => reject(), ONE_MINUTE),
          resolve,
          reject,
        };

        postIFrameMessage(
          iFrame,
          targetOrigin,
          IFrameMessageName.SaveDocument,
          { messageId: saveRequestRef.current.id }
        );
      }),
    saveProgress: () =>
      postIFrameMessage(iFrame, targetOrigin, IFrameMessageName.SaveProgress),
  }));

  return (
    <StyledIFrame
      id={id}
      className={className}
      ref={setIFrame}
      src={url}
      title={title}
      name={name}
      onLoad={onLoad}
      onError={onError}
    />
  );
});

export { ZDocsEditor };
